/**
 * Gmail API utilities for creating drafts
 */

/** Strip basic HTML from Gmail send-as signature for plain-text drafts. */
function htmlSignatureToPlainText(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Primary send-as signature from Gmail settings (plain text).
 * Requires `gmail.readonly` on the OAuth token. Returns null if unavailable.
 */
export async function getGmailSendAsSignaturePlain(accessToken: string): Promise<string | null> {
  try {
    const res = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/settings/sendAs', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      sendAs?: Array<{ signature?: string; isPrimary?: boolean }>;
    };
    const list = data.sendAs;
    if (!list?.length) return null;
    const primary = list.find((s) => s.isPrimary) ?? list[0];
    const html = primary?.signature;
    if (!html || typeof html !== 'string') return null;
    const plain = htmlSignatureToPlainText(html);
    return plain.length > 0 ? plain : null;
  } catch {
    return null;
  }
}

/**
 * Append TractionBee profile signature and (if different) Gmail send-as signature to the message body.
 * API-created drafts do not get Gmail's automatic compose signature; this approximates it.
 */
export function mergeEmailSignaturesForDraft(
  body: string,
  profile: { email_signature?: string | null } | null | undefined,
  gmailPlain: string | null
): string {
  let out = body.replace(/\s+$/, '');
  const p = (profile?.email_signature || '').trim();
  const g = (gmailPlain || '').trim();

  if (p) {
    out += '\n\n' + p;
  }
  if (g) {
    const pNorm = p.replace(/\s+/g, ' ');
    const gNorm = g.replace(/\s+/g, ' ');
    const likelyDuplicate =
      pNorm === gNorm ||
      (pNorm.length > 15 &&
        gNorm.length > 15 &&
        (pNorm.includes(gNorm.slice(0, Math.min(80, gNorm.length))) ||
          gNorm.includes(pNorm.slice(0, Math.min(80, pNorm.length)))));
    if (!likelyDuplicate) {
      out += '\n\n' + g;
    }
  }
  return out;
}

interface GmailDraftResponse {
  success: boolean;
  draftId?: string;
  error?: string;
}

/** Result of checking Gmail Sent for a message to this recipient after a timestamp. */
export type GmailSentLookupResult = 'found' | 'not_found' | 'auth_error' | 'error';

/**
 * Detects whether the user sent mail to `recipientEmail` after `notBeforeEpochMs` (Gmail internalDate).
 * Uses Gmail search `in:sent to:email` and compares message internalDate. Requires `gmail.readonly`.
 */
export async function findSentMessageToRecipientAfter(
  accessToken: string,
  recipientEmail: string,
  notBeforeEpochMs: number
): Promise<GmailSentLookupResult> {
  const trimmed = recipientEmail.trim();
  if (!trimmed) return 'not_found';

  const q = `in:sent to:${trimmed}`;
  try {
    const listUrl = `https://gmail.googleapis.com/gmail/v1/users/me/messages?q=${encodeURIComponent(q)}&maxResults=15`;
    const listRes = await fetch(listUrl, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (listRes.status === 401 || listRes.status === 403) {
      return 'auth_error';
    }
    if (!listRes.ok) {
      console.warn('Gmail messages.list failed:', listRes.status);
      return 'error';
    }
    const listData = (await listRes.json()) as { messages?: Array<{ id: string }> };
    const ids = listData.messages?.map((m) => m.id) ?? [];
    if (ids.length === 0) return 'not_found';

    for (const id of ids) {
      const msgRes = await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${encodeURIComponent(id)}?format=metadata&fields=id,internalDate`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (msgRes.status === 401 || msgRes.status === 403) {
        return 'auth_error';
      }
      if (!msgRes.ok) continue;
      const msgData = (await msgRes.json()) as { internalDate?: string };
      const internalMs = msgData.internalDate ? parseInt(msgData.internalDate, 10) : NaN;
      if (Number.isFinite(internalMs) && internalMs >= notBeforeEpochMs) {
        return 'found';
      }
    }
    return 'not_found';
  } catch (e) {
    console.warn('findSentMessageToRecipientAfter:', e);
    return 'error';
  }
}

/**
 * Creates a draft in Gmail using the Gmail API
 */
export async function createGmailDraft(
  accessToken: string,
  to: string,
  subject: string,
  body: string
): Promise<GmailDraftResponse> {
  try {
    // Create RFC 2822 formatted email
    const emailContent = [
      `To: ${to}`,
      `Subject: ${subject}`,
      'Content-Type: text/plain; charset=utf-8',
      '',
      body,
    ].join('\r\n');

    // Base64url encode the email
    const encodedEmail = btoa(unescape(encodeURIComponent(emailContent)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/drafts', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: {
          raw: encodedEmail,
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Gmail API error:', response.status, errorData);
      return {
        success: false,
        error: errorData.error?.message || `Gmail API error: ${response.status}`,
      };
    }

    const data = await response.json();
    return {
      success: true,
      draftId: data.id,
    };
  } catch (error: any) {
    console.error('Failed to create Gmail draft:', error);
    return {
      success: false,
      error: error.message || 'Failed to create Gmail draft',
    };
  }
}
